/**
 * Wilderness RV Mattress - Save & Share Build Feature
 * Integrates with Custom Builder V2 and Klaviyo
 * 
 * Upload to: assets/save-build.js
 * 
 * Reads builder state directly from DOM elements (no builder modification needed).
 * Restores builds by programmatically triggering builder controls.
 */

const WRVSaveBuild = (function() {

  // ============================================================
  // CONFIG
  // ============================================================
  const CONFIG = {
    buildParamKey: 'build',
    maxEmails: 5,
    klaviyoEvent: 'Custom Build Saved',
    builderPagePath: '/pages/custom-builder-v2',
  };

  // Mattress display names (mirrors builder's NM object)
  const MATT_NAMES = {
    montana: 'Montana Hybrid',
    aspen: 'Aspen',
    canyon: 'Canyon',
    dreambunk: 'Dream Bunk'
  };

  // Default thicknesses (mirrors builder's TH object)
  const MATT_THICKNESS = {
    montana: 11,
    aspen: 10,
    canyon: 8,
    dreambunk: 7
  };

  // ============================================================
  // READ BUILD STATE FROM DOM
  // ============================================================

  /**
   * Reads the current build configuration directly from the builder's DOM.
   * This avoids needing to modify the builder's internal IIFE state.
   */
  function getCurrentBuildConfig() {
    try {
      // Mattress type: whichever .matt-card has .sel class
      const selMattCard = document.querySelector('.matt-card.sel');
      const mattType = selMattCard ? selMattCard.dataset.matt : 'montana';

      // Comfort level: whichever .comf-btn-sm has .sel class
      const selComfBtn = document.querySelector('.comf-btn-sm.sel');
      const comfLevel = selComfBtn ? selComfBtn.dataset.comf : 'plush';

      // Dimensions from inputs
      const width = parseInt(document.getElementById('inpW')?.value) || 60;
      const length = parseInt(document.getElementById('inpL')?.value) || 80;

      // Corner type: check which .corn-tog-btn has .act class
      const custSec = document.getElementById('custSec');
      const cornType = (custSec && custSec.classList.contains('act')) ? 'cust' : 'std';

      // V2: Per-corner configs — each corner has its own style and dimensions
      // Read from .corn-editor elements with data-corner attribute
      const cornerKeys = ['tl', 'tr', 'bl', 'br'];
      const corners = {};
      const cornersV2 = {};
      cornerKeys.forEach(function(key) {
        var editor = document.querySelector('.corn-editor[data-corner="' + key + '"]');
        if (editor) {
          var selBtn = editor.querySelector('.corn-style-btn.sel');
          var style = selBtn ? (selBtn.getAttribute('data-style') || 'none') : 'none';
          var w = parseInt(document.getElementById('cornW_' + key)?.value) || 10;
          var d = parseInt(document.getElementById('cornD_' + key)?.value) || 10;
          cornersV2[key] = { style: style, w: w, d: d };
          // Legacy compat: corner is "active" if style is not 'none'
          corners[key] = style !== 'none';
        } else {
          cornersV2[key] = { style: 'none', w: 10, d: 10 };
          corners[key] = false;
        }
      });

      // Legacy fields (for backward compat with V1 URLs)
      // Use first active corner's style/dims as the shared value
      var firstActive = cornerKeys.find(function(k) { return cornersV2[k].style !== 'none'; });
      const cornStyle = firstActive ? cornersV2[firstActive].style : 'radius';
      const cornWidth = firstActive ? cornersV2[firstActive].w : 10;
      const cornDepth = firstActive ? cornersV2[firstActive].d : 10;

      // Hinge: which .hinge-btn has .sel
      const selHingeBtn = document.querySelector('.hinge-btn.sel');
      const hingeType = selHingeBtn ? selHingeBtn.dataset.hinge : 'none';

      // Hinge position from slider
      const hingePos = parseInt(document.getElementById('hingeSlider')?.value) || 40;

      // Thickness type: which .thick-btn has .sel
      const selThickBtn = document.querySelector('.thick-btn.sel');
      const thickType = selThickBtn ? selThickBtn.dataset.thick : 'same';

      // Thickness value
      const thickVal = parseInt(document.getElementById('inpThick')?.value) || MATT_THICKNESS[mattType];

      // Price from display
      const priceEl = document.getElementById('priceCur');
      const priceText = priceEl ? priceEl.textContent.replace(/[^0-9.,]/g, '') : '';

      return {
        matt: mattType,
        mattName: MATT_NAMES[mattType] || mattType,
        comf: comfLevel,
        w: width,
        l: length,
        cornType: cornType,
        cornStyle: cornStyle,
        corns: corners,
        cornersV2: cornersV2,
        cw: cornWidth,
        cd: cornDepth,
        hinge: hingeType,
        hingePos: hingePos,
        thickType: thickType,
        thickVal: thickVal,
        price: priceText,
        savedAt: new Date().toISOString(),
        v: 2
      };
    } catch (e) {
      console.error('WRVSaveBuild: Error reading build state', e);
      return null;
    }
  }

  // ============================================================
  // URL ENCODING / DECODING
  // ============================================================

  function encodeBuild(config) {
    try {
      var json = JSON.stringify(config);
      var encoded = btoa(unescape(encodeURIComponent(json)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
      return encoded;
    } catch (e) {
      console.error('WRVSaveBuild: Encode error', e);
      return null;
    }
  }

  function decodeBuild(encoded) {
    try {
      var base64 = encoded
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      while (base64.length % 4) base64 += '=';
      var json = decodeURIComponent(escape(atob(base64)));
      return JSON.parse(json);
    } catch (e) {
      console.error('WRVSaveBuild: Decode error', e);
      return null;
    }
  }

  function generateBuildURL(config) {
    var encoded = encodeBuild(config);
    if (!encoded) return null;
    var baseURL = window.location.origin + CONFIG.builderPagePath;
    return baseURL + '?build=' + encoded;
  }

  // ============================================================
  // KLAVIYO INTEGRATION
  // ============================================================

  function sendToKlaviyo(email, buildConfig, buildURL, isOwner) {
    var _learnq = window._learnq || [];

    // Identify the profile in Klaviyo
    _learnq.push(['identify', { '$email': email }]);

    // Build a human-readable summary
    var summary = buildSummaryText(buildConfig);

    // Corner description for email
    var cornerDesc = 'Standard (square)';
    if (buildConfig.cornType === 'cust') {
      var activeCorners = [];
      if (buildConfig.corns.tl) activeCorners.push('Top-Left');
      if (buildConfig.corns.tr) activeCorners.push('Top-Right');
      if (buildConfig.corns.bl) activeCorners.push('Bottom-Left');
      if (buildConfig.corns.br) activeCorners.push('Bottom-Right');
      if (activeCorners.length > 0) {
        cornerDesc = buildConfig.cornStyle.charAt(0).toUpperCase() + buildConfig.cornStyle.slice(1) +
          ' (' + activeCorners.join(', ') + ') — ' + buildConfig.cw + '" x ' + buildConfig.cd + '"';
      } else {
        cornerDesc = 'Custom (none selected)';
      }
    }

    // Hinge description
    var hingeDesc = 'None';
    if (buildConfig.hinge === 'horiz') {
      hingeDesc = 'Horizontal — ' + buildConfig.hingePos + '" from foot';
    } else if (buildConfig.hinge === 'vert') {
      hingeDesc = 'Vertical — ' + buildConfig.hingePos + '" from left';
    }

    // Thickness description
    var thickDesc = MATT_THICKNESS[buildConfig.matt] + '"';
    if (buildConfig.hinge !== 'none') {
      if (buildConfig.thickType === 'same') {
        thickDesc += ' (hinged section same)';
      } else {
        thickDesc += ' (hinged section: ' + buildConfig.thickVal + '")';
      }
    }

    // Track the event with all build details
    _learnq.push(['track', CONFIG.klaviyoEvent, {
      'Mattress Type': buildConfig.mattName,
      'Comfort Level': buildConfig.matt === 'montana' ? (buildConfig.comf.charAt(0).toUpperCase() + buildConfig.comf.slice(1)) : 'N/A',
      'Width': buildConfig.w,
      'Length': buildConfig.l,
      'Width x Length': buildConfig.w + '" x ' + buildConfig.l + '"',
      'Square Feet': (buildConfig.w * buildConfig.l / 144).toFixed(1),
      'Thickness': thickDesc,
      'Corners': cornerDesc,
      'Hinge': hingeDesc,
      'Price': buildConfig.price ? '$' + buildConfig.price : 'See builder',
      'Build Summary': summary,
      'Build URL': buildURL,
      'Is Owner': isOwner,
      'Saved At': buildConfig.savedAt,
      'Build Version': buildConfig.v,
      // Raw data for advanced segmentation
      'Raw Mattress Key': buildConfig.matt,
      'Raw Corner Type': buildConfig.cornType,
      'Raw Hinge Type': buildConfig.hinge,
      'Has Custom Corners': buildConfig.cornType === 'cust',
      'Has Hinge': buildConfig.hinge !== 'none'
    }]);
  }

  function buildSummaryText(config) {
    var parts = [];
    parts.push(config.mattName);
    if (config.matt === 'montana') {
      parts.push(config.comf.charAt(0).toUpperCase() + config.comf.slice(1));
    }
    parts.push(config.w + '" x ' + config.l + '"');
    if (config.cornType === 'cust') {
      var n = 0;
      for (var k in config.corns) { if (config.corns[k]) n++; }
      if (n > 0) parts.push(n + ' custom corner' + (n > 1 ? 's' : ''));
    }
    if (config.hinge !== 'none') {
      parts.push('Hinged (' + (config.hinge === 'horiz' ? 'horizontal' : 'vertical') + ')');
    }
    if (config.price) parts.push('$' + config.price);
    return parts.join(' | ');
  }

  // ============================================================
  // MODAL UI
  // ============================================================

  function createModal() {
    if (document.getElementById('saveBuildModal')) return;

    var modal = document.createElement('div');
    modal.id = 'saveBuildModal';
    modal.className = 'sb-modal-overlay';
    modal.innerHTML =
      '<div class="sb-modal">' +
        '<button class="sb-close" onclick="WRVSaveBuild.closeModal()" aria-label="Close">' +
          '<svg viewBox="0 0 24 24" width="20" height="20"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>' +

        // Step 1: Email entry
        '<div class="sb-modal-content" id="sbStep1">' +
          '<div class="sb-icon">' +
            '<svg viewBox="0 0 24 24" width="40" height="40"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" fill="none" stroke="currentColor" stroke-width="1.5"/><polyline points="17 21 17 13 7 13 7 21" fill="none" stroke="currentColor" stroke-width="1.5"/><polyline points="7 3 7 8 15 8" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>' +
          '</div>' +
          '<h3>Save & Share Your Build</h3>' +
          '<p class="sb-subtitle">We\'ll email a link to your custom mattress configuration so you (or someone else) can pick up right where you left off.</p>' +

          '<div class="sb-build-preview" id="sbBuildPreview"></div>' +

          '<div class="sb-email-section">' +
            '<label class="sb-label">Email address(es)</label>' +
            '<div class="sb-email-list" id="sbEmailList">' +
              '<div class="sb-email-row">' +
                '<input type="email" class="sb-email-input" placeholder="your@email.com" required>' +
                '<span class="sb-email-tag sb-tag-me">You</span>' +
              '</div>' +
            '</div>' +
            '<button class="sb-add-email" id="sbAddEmail" onclick="WRVSaveBuild.addEmailRow()">' +
              '<svg viewBox="0 0 24 24" width="16" height="16"><line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" stroke-width="2"/><line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" stroke-width="2"/></svg>' +
              'Share with someone else' +
            '</button>' +
          '</div>' +

          '<div class="sb-error" id="sbError" style="display:none"></div>' +

          '<button class="sb-send-btn" onclick="WRVSaveBuild.handleSend()">' +
            '<svg viewBox="0 0 24 24" width="18" height="18"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
            'Send Build Link' +
          '</button>' +
        '</div>' +

        // Step 2: Success
        '<div class="sb-modal-content sb-success" id="sbStep2" style="display:none">' +
          '<div class="sb-icon sb-icon-success">' +
            '<svg viewBox="0 0 24 24" width="48" height="48"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M8 12l3 3 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '</div>' +
          '<h3>Build Saved!</h3>' +
          '<p class="sb-subtitle" id="sbSuccessMsg">We\'ve sent your custom mattress build to the email(s) provided.</p>' +

          '<div class="sb-link-copy">' +
            '<label class="sb-label">Or copy the direct link:</label>' +
            '<div class="sb-copy-row">' +
              '<input type="text" class="sb-link-input" id="sbLinkInput" readonly>' +
              '<button class="sb-copy-btn" onclick="WRVSaveBuild.copyLink()">' +
                '<svg viewBox="0 0 24 24" width="16" height="16"><rect x="9" y="9" width="13" height="13" rx="2" fill="none" stroke="currentColor" stroke-width="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" fill="none" stroke="currentColor" stroke-width="2"/></svg>' +
                '<span id="sbCopyText">Copy</span>' +
              '</button>' +
            '</div>' +
          '</div>' +

          '<div class="sb-success-actions">' +
            '<button class="sb-done-btn" onclick="WRVSaveBuild.closeModal()">Continue Building</button>' +
          '</div>' +
        '</div>' +

        // Loading state
        '<div class="sb-modal-content sb-loading" id="sbLoading" style="display:none">' +
          '<div class="sb-spinner"></div>' +
          '<p>Sending your build...</p>' +
        '</div>' +
      '</div>';

    // Close on overlay click
    modal.addEventListener('click', function(e) {
      if (e.target === modal) WRVSaveBuild.closeModal();
    });

    // Close on Escape
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') WRVSaveBuild.closeModal();
    });

    document.body.appendChild(modal);
  }

  function openModal() {
    createModal();
    var config = getCurrentBuildConfig();
    if (!config) {
      alert('Unable to read your current build. Please try again.');
      return;
    }

    // Show build preview
    var preview = document.getElementById('sbBuildPreview');
    if (preview) preview.innerHTML = buildPreviewHTML(config);

    // Reset to step 1
    document.getElementById('sbStep1').style.display = '';
    document.getElementById('sbStep2').style.display = 'none';
    document.getElementById('sbLoading').style.display = 'none';
    document.getElementById('sbError').style.display = 'none';

    // Reset emails
    var emailList = document.getElementById('sbEmailList');
    emailList.innerHTML =
      '<div class="sb-email-row">' +
        '<input type="email" class="sb-email-input" placeholder="your@email.com" required>' +
        '<span class="sb-email-tag sb-tag-me">You</span>' +
      '</div>';
    document.getElementById('sbAddEmail').style.display = '';

    // Show modal
    document.getElementById('saveBuildModal').classList.add('active');
    document.body.style.overflow = 'hidden';

    // Focus first input
    setTimeout(function() {
      var firstInput = document.querySelector('#saveBuildModal .sb-email-input');
      if (firstInput) firstInput.focus();
    }, 200);
  }

  function closeModal() {
    var modal = document.getElementById('saveBuildModal');
    if (modal) {
      modal.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  function buildPreviewHTML(config) {
    var items = [];
    items.push('<div class="sb-prev-item"><span class="sb-prev-label">Mattress</span><span class="sb-prev-value">' + escapeHTML(config.mattName) + '</span></div>');
    if (config.matt === 'montana') {
      items.push('<div class="sb-prev-item"><span class="sb-prev-label">Comfort</span><span class="sb-prev-value">' + escapeHTML(config.comf.charAt(0).toUpperCase() + config.comf.slice(1)) + '</span></div>');
    }
    items.push('<div class="sb-prev-item"><span class="sb-prev-label">Size</span><span class="sb-prev-value">' + config.w + '" &times; ' + config.l + '"</span></div>');
    if (config.cornType === 'cust') {
      var n = 0;
      for (var k in config.corns) { if (config.corns[k]) n++; }
      if (n > 0) items.push('<div class="sb-prev-item"><span class="sb-prev-label">Corners</span><span class="sb-prev-value">' + n + ' ' + escapeHTML(config.cornStyle) + '</span></div>');
    }
    if (config.hinge !== 'none') {
      items.push('<div class="sb-prev-item"><span class="sb-prev-label">Hinge</span><span class="sb-prev-value">' + (config.hinge === 'horiz' ? 'Horizontal' : 'Vertical') + '</span></div>');
    }
    if (config.price) {
      items.push('<div class="sb-prev-item"><span class="sb-prev-label">Price</span><span class="sb-prev-value">$' + escapeHTML(config.price) + '</span></div>');
    }
    return items.join('');
  }

  function addEmailRow() {
    var list = document.getElementById('sbEmailList');
    var rows = list.querySelectorAll('.sb-email-row');
    if (rows.length >= CONFIG.maxEmails) return;

    var row = document.createElement('div');
    row.className = 'sb-email-row';
    row.innerHTML =
      '<input type="email" class="sb-email-input" placeholder="partner@email.com">' +
      '<button class="sb-remove-email" onclick="this.parentElement.remove(); WRVSaveBuild.checkAddButton();" aria-label="Remove">' +
        '<svg viewBox="0 0 24 24" width="16" height="16"><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2"/></svg>' +
      '</button>';
    list.appendChild(row);
    row.querySelector('.sb-email-input').focus();
    checkAddButton();
  }

  function checkAddButton() {
    var list = document.getElementById('sbEmailList');
    var btn = document.getElementById('sbAddEmail');
    if (list && btn) {
      btn.style.display = list.querySelectorAll('.sb-email-row').length >= CONFIG.maxEmails ? 'none' : '';
    }
  }

  // ============================================================
  // SEND LOGIC
  // ============================================================

  function handleSend() {
    var errorEl = document.getElementById('sbError');
    errorEl.style.display = 'none';

    var inputs = document.querySelectorAll('#sbEmailList .sb-email-input');
    var emails = [];
    var hasInvalid = false;

    inputs.forEach(function(input, i) {
      var val = input.value.trim();
      if (val) {
        if (!isValidEmail(val)) {
          hasInvalid = true;
          input.classList.add('sb-input-error');
        } else {
          input.classList.remove('sb-input-error');
          emails.push({ email: val, isOwner: i === 0 });
        }
      } else if (i === 0) {
        hasInvalid = true;
        input.classList.add('sb-input-error');
      }
    });

    if (hasInvalid) { showError('Please enter valid email address(es).'); return; }
    if (emails.length === 0) { showError('Please enter at least one email address.'); return; }

    var config = getCurrentBuildConfig();
    if (!config) { showError('Unable to read your build. Please try again.'); return; }

    var buildURL = generateBuildURL(config);
    if (!buildURL) { showError('Something went wrong. Please try again.'); return; }

    // Show loading
    document.getElementById('sbStep1').style.display = 'none';
    document.getElementById('sbLoading').style.display = '';

    // Send to Klaviyo for each email with stagger
    var delay = 0;
    emails.forEach(function(entry) {
      setTimeout(function() {
        sendToKlaviyo(entry.email, config, buildURL, entry.isOwner);
      }, delay);
      delay += 300;
    });

    // Show success
    setTimeout(function() {
      showSuccess(emails, buildURL);
    }, delay + 500);
  }

  function showSuccess(emails, buildURL) {
    document.getElementById('sbLoading').style.display = 'none';
    document.getElementById('sbStep2').style.display = '';

    var msg = document.getElementById('sbSuccessMsg');
    if (emails.length === 1) {
      msg.textContent = 'We\'ve sent your custom mattress build to ' + emails[0].email;
    } else {
      msg.textContent = 'We\'ve sent your custom mattress build to ' + emails.length + ' email addresses.';
    }

    document.getElementById('sbLinkInput').value = buildURL;
  }

  function copyLink() {
    var input = document.getElementById('sbLinkInput');
    var btnText = document.getElementById('sbCopyText');
    if (navigator.clipboard) {
      navigator.clipboard.writeText(input.value).then(function() {
        btnText.textContent = 'Copied!';
        setTimeout(function() { btnText.textContent = 'Copy'; }, 2000);
      });
    } else {
      input.select();
      document.execCommand('copy');
      btnText.textContent = 'Copied!';
      setTimeout(function() { btnText.textContent = 'Copy'; }, 2000);
    }
  }

  function showError(message) {
    var el = document.getElementById('sbError');
    el.textContent = message;
    el.style.display = '';
  }

  // ============================================================
  // BUILD RESTORATION FROM URL
  // ============================================================

  function checkForSavedBuild() {
    var params = new URLSearchParams(window.location.search);
    var buildParam = params.get(CONFIG.buildParamKey);
    if (!buildParam) return null;

    var config = decodeBuild(buildParam);
    if (!config) {
      console.warn('WRVSaveBuild: Invalid build parameter');
      return null;
    }
    return config;
  }

  /**
   * Helper: dispatch a real click event on an element.
   * This triggers onclick handlers with a proper event object,
   * which is required for builder functions that use event.target.
   */
  function simulateClick(el) {
    if (!el) return;
    el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }

  /**
   * Restores a saved build by simulating real clicks on DOM elements.
   * This ensures builder functions that rely on event.target work correctly.
   */
  function restoreBuild(config) {
    if (!config) return false;
    console.log('WRVSaveBuild: Restoring config:', JSON.stringify(config, null, 2));

    try {
      // 1. Select mattress type
      var mattCard = document.querySelector('.matt-card[data-matt="' + config.matt + '"]');
      console.log('WRVSaveBuild: [1] Mattress card for "' + config.matt + '":', mattCard ? 'FOUND' : 'NOT FOUND');
      if (mattCard) simulateClick(mattCard);

      // 2. Set comfort level (Montana only)
      if (config.matt === 'montana' && config.comf) {
        var comfBtn = document.querySelector('.comf-btn-sm[data-comf="' + config.comf + '"]');
        if (!comfBtn) comfBtn = document.querySelector('.comf-btn[data-comf="' + config.comf + '"]');
        console.log('WRVSaveBuild: [2] Comfort btn:', comfBtn ? 'FOUND' : 'NOT FOUND');
        if (comfBtn) simulateClick(comfBtn);
      }

      // 3. Set dimensions
      console.log('WRVSaveBuild: [3] Setting size:', config.w, 'x', config.l);
      if (config.w && config.l && window.WRVBuilder && window.WRVBuilder.setSize) {
        window.WRVBuilder.setSize(config.w, config.l);
      }

      // 4. Set corner type
      if (config.cornType === 'cust') {
        var togBtns = document.querySelectorAll('.corn-tog-btn');
        console.log('WRVSaveBuild: [4] Corner toggle btns found:', togBtns.length);
        togBtns.forEach(function(btn, i) {
          console.log('  btn[' + i + ']:', btn.textContent.trim().substring(0, 30), '| classes:', btn.className);
        });
        var custToggle = togBtns.length > 1 ? togBtns[1] : togBtns[0];
        if (custToggle) {
          console.log('WRVSaveBuild: [4] Clicking custom toggle');
          simulateClick(custToggle);
        }

        setTimeout(function() {
          // V2 per-corner restore: each corner has its own editor with style + dimensions
          var cornerKeys = ['tl', 'tr', 'bl', 'br'];

          // Determine per-corner config, supporting both V2 (cornersV2) and legacy V1 formats
          cornerKeys.forEach(function(key) {
            var editor = document.querySelector('.corn-editor[data-corner="' + key + '"]');
            if (!editor) {
              console.log('WRVSaveBuild: [4b] corn-editor for "' + key + '" NOT FOUND');
              return;
            }

            var targetStyle = 'none';
            var targetW = 10;
            var targetD = 10;

            if (config.cornersV2 && config.cornersV2[key]) {
              // V2 format: per-corner style and dimensions
              targetStyle = config.cornersV2[key].style || 'none';
              targetW = config.cornersV2[key].w || 10;
              targetD = config.cornersV2[key].d || 10;
            } else if (config.corns && config.corns[key]) {
              // Legacy V1 format: shared style applied to active corners
              targetStyle = config.cornStyle || 'radius';
              targetW = config.cw || 10;
              targetD = config.cd || 10;
            }
            // else: corner stays at 'none' (default)

            console.log('WRVSaveBuild: [4b] Corner "' + key + '": target style=' + targetStyle + ' w=' + targetW + ' d=' + targetD);

            // Click the matching style button within this corner's editor
            var styleBtn = editor.querySelector('.corn-style-btn[data-style="' + targetStyle + '"]');
            if (styleBtn) {
              console.log('WRVSaveBuild: [4c] Clicking style btn for "' + key + '" -> "' + targetStyle + '"');
              simulateClick(styleBtn);
            } else {
              console.log('WRVSaveBuild: [4c] Style btn NOT FOUND for "' + key + '" style "' + targetStyle + '"');
            }

            // Set per-corner dimensions if style is not 'none'
            if (targetStyle !== 'none') {
              var wInput = document.getElementById('cornW_' + key);
              var dInput = document.getElementById('cornD_' + key);
              console.log('WRVSaveBuild: [4d] Dims for "' + key + '": cornW_' + key + '=' + (wInput ? 'FOUND' : 'NOT FOUND') + ', cornD_' + key + '=' + (dInput ? 'FOUND' : 'NOT FOUND'));
              if (wInput) {
                wInput.value = targetW;
                wInput.dispatchEvent(new Event('input', { bubbles: true }));
              }
              if (dInput) {
                dInput.value = targetD;
                dInput.dispatchEvent(new Event('input', { bubbles: true }));
              }
              // Update builder state for this corner
              if (window.WRVBuilder && window.WRVBuilder.updCornDimFor) {
                window.WRVBuilder.updCornDimFor(key);
              }
            }
          });

          if (window.WRVBuilder && window.WRVBuilder.updPrev) window.WRVBuilder.updPrev();
        }, 150);
      }

      // 5. Set hinge
      if (config.hinge && config.hinge !== 'none') {
        setTimeout(function() {
          var hingeEl = document.querySelector('.hinge-btn[data-hinge="' + config.hinge + '"]');
          console.log('WRVSaveBuild: [5] Hinge btn for "' + config.hinge + '":', hingeEl ? 'FOUND (' + hingeEl.className.substring(0, 40) + ')' : 'NOT FOUND');

          // Log all hinge-related elements for debugging
          console.log('WRVSaveBuild: [5] .hinge-btn elements:', document.querySelectorAll('.hinge-btn').length);
          document.querySelectorAll('[data-hinge]').forEach(function(el) {
            console.log('  data-hinge="' + el.dataset.hinge + '" tag=' + el.tagName + ' class="' + el.className.substring(0, 50) + '"');
          });

          if (hingeEl) {
            // Prefer direct API call since selHinge expects the element as param
            if (window.WRVBuilder && window.WRVBuilder.selHinge) {
              window.WRVBuilder.selHinge(hingeEl);
            } else {
              simulateClick(hingeEl);
            }
          }

          setTimeout(function() {
            if (config.hingePos) {
              var slider = document.getElementById('hingeSlider');
              console.log('WRVSaveBuild: [5b] hingeSlider:', slider ? 'FOUND' : 'NOT FOUND');
              if (slider) {
                slider.value = config.hingePos;
                slider.dispatchEvent(new Event('input', { bubbles: true }));
                if (window.WRVBuilder && window.WRVBuilder.updHingePos) {
                  window.WRVBuilder.updHingePos();
                }
              }
            }

            if (config.thickType && config.thickType !== 'same') {
              var thickBtn = document.querySelector('.thick-btn[data-thick="' + config.thickType + '"]');
              console.log('WRVSaveBuild: [5c] Thick btn:', thickBtn ? 'FOUND' : 'NOT FOUND');
              if (thickBtn) simulateClick(thickBtn);

              setTimeout(function() {
                if (config.thickVal) {
                  var thickInput = document.getElementById('inpThick');
                  if (thickInput) {
                    thickInput.value = config.thickVal;
                    thickInput.dispatchEvent(new Event('input', { bubbles: true }));
                    if (window.WRVBuilder && window.WRVBuilder.updThick) {
                      window.WRVBuilder.updThick();
                    }
                  }
                }
                navigateToReview();
              }, 100);
            } else {
              navigateToReview();
            }
          }, 150);
        }, 200);
      } else {
        setTimeout(function() {
          navigateToReview();
        }, config.cornType === 'cust' ? 400 : 100);
      }

      return true;
    } catch (e) {
      console.error('WRVSaveBuild: Restore failed', e);
      return false;
    }
  }

  /**
   * Navigate to step 5 (review) after all settings are restored
   */
  function navigateToReview() {
    if (window.WRVBuilder && window.WRVBuilder.goTo) {
      for (var step = 2; step <= 5; step++) {
        window.WRVBuilder.goTo(step);
      }
    }
    if (window.WRVBuilder && window.WRVBuilder.updPrev) {
      window.WRVBuilder.updPrev();
    }
  }

  /**
   * Shows a banner when a saved build has been restored
   */
  function showRestoreBanner() {
    var banner = document.createElement('div');
    banner.className = 'sb-restore-banner';
    banner.innerHTML =
      '<div class="sb-restore-content">' +
        '<svg viewBox="0 0 24 24" width="20" height="20"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M8 12l3 3 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '<span>Your saved build has been restored! Review your configuration and add to cart when ready.</span>' +
        '<button onclick="this.closest(\'.sb-restore-banner\').remove()">' +
          '<svg viewBox="0 0 24 24" width="16" height="16"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>' +
      '</div>';

    var builder = document.querySelector('.builder-v2-wrapper') || document.querySelector('.builder-container') || document.querySelector('.builder-v2-page');
    if (builder) {
      builder.insertBefore(banner, builder.firstChild);
    } else {
      document.body.insertBefore(banner, document.body.firstChild);
    }
  }

  // ============================================================
  // HELPERS
  // ============================================================

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function escapeHTML(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ============================================================
  // INITIALIZATION
  // ============================================================

  function init() {
    var savedConfig = checkForSavedBuild();
    if (savedConfig) {
      // Wait for builder to be ready
      var attempts = 0;
      var waitForBuilder = setInterval(function() {
        attempts++;
        if (window.WRVBuilder && document.querySelector('.matt-card')) {
          clearInterval(waitForBuilder);
          // Small delay to let builder fully initialize
          setTimeout(function() {
            var restored = restoreBuild(savedConfig);
            if (restored) {
              showRestoreBanner();
              // Clean URL without reloading
              var cleanURL = window.location.origin + window.location.pathname;
              window.history.replaceState({}, '', cleanURL);
            }
          }, 300);
        }
        if (attempts > 50) clearInterval(waitForBuilder); // 10s timeout
      }, 200);
    }
  }

  // Auto-init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ============================================================
  // PUBLIC API
  // ============================================================
  return {
    openModal: openModal,
    closeModal: closeModal,
    addEmailRow: addEmailRow,
    checkAddButton: checkAddButton,
    handleSend: handleSend,
    copyLink: copyLink,
    init: init,
    checkForSavedBuild: checkForSavedBuild,
    restoreBuild: restoreBuild,
    getCurrentBuildConfig: getCurrentBuildConfig,
    generateBuildURL: generateBuildURL
  };

})();